import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { InlineLoader } from "@/components/shared";
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { TrendingUp, Share2, ArrowUp, ArrowDown, Minus } from "lucide-react";

const fmtDate = (d) => {
  const [, mm, dd] = (d || "").split("-");
  return mm && dd ? `${Number(mm)}/${Number(dd)}` : d;
};

function TrendArrow({ delta, prev, hasCurrent }) {
  if (delta === null || delta === undefined) {
    if (!hasCurrent) return null;
    return (
      <span
        data-testid="conversion-trend-arrow"
        aria-label="No prior period data to compare against"
        className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] px-2 py-0.5 text-xs font-bold text-ff-medium"
      >
        <Minus className="h-3.5 w-3.5" aria-hidden="true" /> No prior data
      </span>
    );
  }
  const up = delta > 0;
  const flat = delta === 0;
  const Icon = flat ? Minus : up ? ArrowUp : ArrowDown;
  const cls = flat ? "text-ff-medium bg-[#F3F4F6]" : up ? "text-ff-green bg-[#ECFDF5]" : "text-ff-red bg-[#FEF2F2]";
  const label = flat
    ? "No change in conversion rate vs the prior period"
    : `Conversion rate ${up ? "up" : "down"} ${Math.abs(delta)} points vs the prior period`;
  return (
    <span
      data-testid="conversion-trend-arrow"
      title={prev !== null && prev !== undefined ? `Prior period: ${prev}%` : undefined}
      aria-label={label}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-extrabold ${cls}`}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {flat ? "0" : `${up ? "+" : "−"}${Math.abs(delta)}`} pts
    </span>
  );
}

export default function ShareAnalytics() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("30");

  useEffect(() => {
    api.get(`/admin/share-analytics?days=${range}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [range]);

  return (
    <div className="mt-8 rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="share-analytics-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EFF6FF] text-[#1D4ED8]"><TrendingUp className="h-5 w-5" /></span>
          <div>
            <div className="font-heading font-bold text-ff-blue">Share momentum</div>
            <div className="text-sm text-ff-medium">Campaign shares over time — the top of your growth funnel.</div>
          </div>
        </div>
        <div className="flex gap-1 rounded-lg bg-[#F3F4F6] p-1" data-testid="analytics-range-toggle">
          {["7", "30", "90"].map((r) => (
            <button key={r} onClick={() => setRange(r)} data-testid={`range-${r}`}
              className={`rounded-md px-3 py-1 text-xs font-bold transition-colors duration-200 ${range === r ? "bg-white text-ff-blue shadow-sm" : "text-ff-medium hover:text-ff-blue"}`}>
              {r}d
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="mt-6"><InlineLoader label="Loading share data" /></div>
      ) : (
        <>
          <div className="mt-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-xl bg-gradient-to-r from-[#FEF2F2] to-[#F3F4F6] p-4" data-testid="conversion-rate-band">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-heading text-4xl font-extrabold text-ff-red" data-testid="conversion-rate">
                  {data.conversion_rate !== null && data.conversion_rate !== undefined ? `${Math.round(data.conversion_rate)}%` : "—"}
                </span>
                <TrendArrow delta={data.conversion_delta} prev={data.prev_conversion_rate} hasCurrent={data.conversion_rate !== null && data.conversion_rate !== undefined} />
              </div>
              <div className="mt-0.5 text-sm font-bold text-ff-blue">of shares convert to donations</div>
              <div className="mt-0.5 text-xs text-ff-medium">
                {data.total_donations} donations from {data.total_shares} shares · last {data.days}d
                {data.prev_conversion_rate !== null && data.prev_conversion_rate !== undefined && (
                  <span> · prior {data.days}d: {Math.round(data.prev_conversion_rate)}%</span>
                )}
              </div>
            </div>
            <div className="flex gap-6">
              <div>
                <div className="font-heading text-2xl font-extrabold text-ff-blue" data-testid="analytics-donations-total">{data.total_donations}</div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ff-medium">donations</div>
              </div>
              <div>
                <div className="font-heading text-2xl font-extrabold text-ff-green">${Math.round(data.total_raised || 0).toLocaleString()}</div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ff-medium">raised</div>
              </div>
              <div>
                <div className="font-heading text-2xl font-extrabold text-ff-red" data-testid="analytics-period-total">{data.total_shares}</div>
                <div className="text-[11px] font-semibold uppercase tracking-wide text-ff-medium">shares</div>
              </div>
            </div>
          </div>

          <div
            className="mt-5 h-64 w-full"
            data-testid="share-chart"
            role="img"
            aria-label={`Share momentum chart over the last ${data.days} days: ${data.total_shares} total shares and ${data.total_donations} donations${data.conversion_rate !== null && data.conversion_rate !== undefined ? `, a ${data.conversion_rate}% conversion rate.` : ", no conversion data yet."}`}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data.series} margin={{ top: 8, right: 4, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="shareFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#C62828" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#C62828" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDEFF3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11, fill: "#6B7280" }}
                  minTickGap={24} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} width={30} />
                <YAxis yAxisId="right" orientation="right" allowDecimals={false} tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} width={30} />
                <Tooltip labelFormatter={fmtDate}
                  contentStyle={{ borderRadius: 10, border: "1px solid #E5E7EB", fontSize: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                <Area yAxisId="left" type="monotone" dataKey="shares" name="Shares" stroke="#C62828" strokeWidth={2.5} fill="url(#shareFill)" />
                <Line yAxisId="right" type="monotone" dataKey="donations" name="Donations" stroke="#143257" strokeWidth={2.5} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {data.top_campaigns.length > 0 && (
            <div className="mt-4 border-t border-ff-border pt-4">
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-ff-medium">Most shared · last {data.days}d</div>
              {data.top_campaigns.map((t, i) => (
                <div key={t.slug} className="flex items-center justify-between gap-2 py-1.5 text-sm" data-testid={`top-campaign-${t.slug}`}>
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="w-4 text-center font-heading text-xs font-bold text-ff-medium">{i + 1}</span>
                    <span className="truncate font-semibold text-ff-blue">{t.title}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1 font-bold text-ff-blue"><Share2 className="h-3.5 w-3.5 text-ff-medium" /> {t.shares}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
